import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { analyticsLabelFromString } from '../../../../helpers/adobe-analytics';
import '../download-button';

// Image caption/attribution row: description left, download control right,
// under a hairline. The single implementation of this cluster — blocks style
// the instance from outside (margin, flex) and reach the text through
// ::part(description).
//
// `description` is a prepared HTML string (line breaks already applied);
// `file-url` is the full-resolution image; `placement` labels the download
// event. With file-url set the built-in download-button renders and this
// element assembles the download payload. Blocks that need their own control
// (a bespoke anchor with its own analytics) slot it as [slot='control']
// instead, which replaces the button.
class ImageCaption extends LitElement {
  static get properties() {
    return {
      description: { type: String },
      fileUrl: { type: String, attribute: 'file-url' },
      placement: { type: String },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      css`
        :host {
          padding-top: 1em;
          border-top: 1px solid var(--block-rule-color, #c0c0c0);
          display: flex;
          justify-content: space-between;
        }

        p {
          font-size: var(--fs-caption);
          line-height: var(--lh-caption);
          letter-spacing: var(--ls-caption);
          margin: 0;
        }

        @media (min-width: 600px) {
          p {
            font-size: var(--fs-body-small);
            line-height: var(--lh-body-small);
            letter-spacing: var(--ls-body-small);
          }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.description = '';
    this.fileUrl = '';
    this.placement = '';
  }

  render() {
    return html`
      <span>
        ${this.description
          ? html`
              <p part="description">${unsafeHTML(this.description)}</p>
            `
          : html``}
      </span>
      <span>
        <slot name="control">
          ${this.fileUrl
            ? html`
                <download-button
                  downloadData=${JSON.stringify(this.downloadData)}
                ></download-button>
              `
            : html``}
        </slot>
      </span>
    `;
  }

  get downloadData() {
    return {
      file_format: 'JPG',
      file_name: analyticsLabelFromString(this.description),
      link_url: this.fileUrl,
      link_type: 'IMAGE',
      placement: analyticsLabelFromString(this.placement),
    };
  }
}

customElements.define('image-caption', ImageCaption);

export { ImageCaption };
